/**
 * STACKED caption style (porte 1:1 do edvid). `captions.style: "stacked"`.
 *
 * Look: words stacked vertically, tight, mixing per line — Poppins bold-italic
 * with a white→light-gray gradient, Poppins regular (smaller), Playfair serif
 * bold-italic in the ACCENT (captions.accent, padrão #ff5200), Poppins bold.
 * Emphasis words appear solo; some get a hand-drawn green "pencil" ellipse.
 *
 * Baked SFX: a click on every solo word, a scratch when a word is circled —
 * `${assetBase}/sfx/caption-click.mp3` + caption-scratch.mp3.
 *
 * Data: props.captionCues (caption_style.py do edvid / captionData.ts).
 */
import React, { useMemo } from 'react';
import { AbsoluteFill, Audio, Sequence, interpolate, Easing, useCurrentFrame, useVideoConfig } from 'remotion';
import { PencilOutline } from './PencilOutline';
import { PLAYFAIR, POPPINS } from './fonts';
import { assetUrl, useReels } from './context';
import type { CaptionCue } from './types';

const DEFAULT_ACCENT = '#ff5200';
const WHITE_GRAD: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #ffffff 46%, #cfcfcf 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};
// 0=bold-italic, 1=regular(small), 2=serif-accent, 3=bold
const lineStylesFor = (accent: string): React.CSSProperties[] => [
  { fontFamily: POPPINS, fontWeight: 900, fontStyle: 'italic', ...WHITE_GRAD },
  { fontFamily: POPPINS, fontWeight: 400, fontStyle: 'normal', ...WHITE_GRAD },
  { fontFamily: PLAYFAIR, fontWeight: 900, fontStyle: 'italic', color: accent },
  { fontFamily: POPPINS, fontWeight: 800, fontStyle: 'normal', ...WHITE_GRAD },
];
const SHADOW = 'drop-shadow(0 5px 9px rgba(0,0,0,0.5))';
const SHADOW_STRONG = 'drop-shadow(0 5px 10px rgba(0,0,0,0.55)) drop-shadow(0 2px 3px rgba(0,0,0,0.55))';

const easeOut = Easing.bezier(0.16, 1, 0.3, 1);
const fitFont = (text: string, base: number, avail = 900, factor = 0.59): number => {
  const n = Math.max(1, text.trim().length);
  const est = n * base * factor;
  return est > avail ? Math.floor(avail / (n * factor)) : base;
};

type Word = { text: string; fromMs: number; toMs: number };

const Cue: React.FC<{
  cue: CaptionCue;
  cueDurationFrames: number;
  offsetY: number;
  fontScale: number;
  lineStyles: React.CSSProperties[];
}> = ({ cue, cueDurationFrames, offsetY, fontScale, lineStyles }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const scale = (width / 1080) * fontScale;
  const avail = width - 180;
  const baseY = Math.round(height * offsetY);

  const ENTER = Math.max(3, Math.min(8, Math.floor(cueDurationFrames * 0.45)));
  const EXIT = Math.max(2, Math.min(7, Math.floor(cueDurationFrames * 0.35)));
  const lastLocalStart = Math.max(...cue.lines.flat().map((w) => ((w.fromMs - cue.startMs) / 1000) * fps));
  const exitStart = Math.max(cueDurationFrames - EXIT, Math.min(lastLocalStart + ENTER, cueDurationFrames - 2));
  const exitProg = interpolate(frame, [exitStart, cueDurationFrames], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  let cOpacity = 1;
  let cTranslateY = 0;
  let cBlur = 0;
  if (cue.exit === 'blur_up') {
    cOpacity = 1 - exitProg;
    cTranslateY = -55 * exitProg;
    cBlur = 14 * exitProg;
  } else {
    cOpacity = frame >= cueDurationFrames - 2 ? 0 : 1;
  }

  const wordAnim = (w: Word, strong: boolean) => {
    const localStart = ((w.fromMs - cue.startMs) / 1000) * fps;
    const enter = Math.max(2, Math.min(ENTER, Math.floor(exitStart - localStart - 1)));
    // REGRA DA CAPA: palavra que começa no frame 0 entra PRONTA (ver Main.tsx).
    const p =
      w.fromMs <= 0
        ? 1
        : interpolate(frame, [localStart, localStart + enter], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
            easing: easeOut,
          });
    const eb = (1 - p) * 5;
    return {
      opacity: p,
      translate: `0px ${interpolate(p, [0, 1], [46, 0])}px`,
      filter: `${eb > 0.3 ? `blur(${eb}px) ` : ''}${strong ? SHADOW_STRONG : SHADOW}`,
      localStart,
    };
  };

  const container: React.CSSProperties = {
    opacity: cOpacity,
    translate: `0px ${baseY + cTranslateY}px`,
    filter: cBlur > 0.25 ? `blur(${cBlur}px)` : undefined,
    textAlign: 'center',
  };

  let inner: React.ReactNode = null;
  if (cue.preset === 'STACK_MIXED') {
    inner = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {cue.lines.map((line, li) => {
          const styleIdx = cue.lineStyles?.[li] ?? (li + cue.styleOffset) % lineStyles.length;
          const ls = lineStyles[styleIdx];
          const lineText = line.map((w) => w.text).join(' ');
          let size = fitFont(lineText, 86, avail / scale, 0.58);
          if (styleIdx === 1) size = Math.round(size * 0.72);
          if (styleIdx === 2) size = Math.round(size * 0.95);
          if (cue.lineEmph?.[li]) size = Math.round(size * 1.12);
          if (cue.lineBoost?.[li]) size = Math.round(size * 1.35);
          return (
            <div
              key={li}
              style={{
                fontSize: size * scale,
                letterSpacing: -1.5,
                lineHeight: 1.12,
                marginTop: li === 0 ? 0 : '-0.34em',
                whiteSpace: 'pre',
              }}
            >
              {line.map((w, wi) => {
                const a = wordAnim(w, styleIdx === 1);
                return (
                  <span
                    key={wi}
                    style={{
                      ...ls,
                      display: 'inline-block',
                      padding: '0 0.06em',
                      opacity: a.opacity,
                      translate: a.translate,
                      filter: a.filter,
                    }}
                  >
                    {w.text + (wi < line.length - 1 ? ' ' : '')}
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  } else if (cue.preset === 'SOLO_BIG') {
    const w = cue.lines[0][0];
    const a = wordAnim(w, false);
    inner = (
      <div
        style={{
          ...WHITE_GRAD,
          fontFamily: POPPINS,
          fontWeight: 900,
          fontStyle: 'italic',
          fontSize: fitFont(w.text, 150, avail / scale, 0.6) * scale,
          letterSpacing: -3,
          lineHeight: 1.12,
          padding: '0 0.14em',
          opacity: a.opacity,
          translate: a.translate,
          scale: String(interpolate(a.opacity, [0, 1], [0.88, 1])),
          filter: a.filter,
        }}
      >
        {w.text}
      </div>
    );
  } else {
    const w = cue.lines[0][0];
    const a = wordAnim(w, false);
    const oStart = a.localStart + 2;
    const oEnd = Math.min(oStart + 10, exitStart - 1, cueDurationFrames - 2);
    const outlineProg = interpolate(frame, [oStart, Math.max(oEnd, oStart + 3)], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
    inner = (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <PencilOutline progress={outlineProg} />
        <span
          style={{
            ...WHITE_GRAD,
            fontFamily: POPPINS,
            fontWeight: 900,
            fontSize: fitFont(w.text, 118, (avail - 80) / scale, 0.6) * scale,
            letterSpacing: -2,
            lineHeight: 1.12,
            padding: '0 0.1em',
            opacity: a.opacity,
            translate: a.translate,
            filter: a.filter,
            display: 'inline-block',
          }}
        >
          {w.text}
        </span>
      </div>
    );
  }

  return (
    <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center', padding: '0 90px' }}>
      <div style={container}>{inner}</div>
    </AbsoluteFill>
  );
};

export const StackedCaptions: React.FC = () => {
  const { fps, durationInFrames } = useVideoConfig();
  const { captionCues, editData, assetBase } = useReels();
  const CAP = editData.captions;
  const offsetY = CAP.stackedOffsetY ?? 0.156; // fraction of height, below center
  const fontScale = CAP.fontScale ?? 0.8;
  const SFX = CAP.sfx ?? { enabled: true };
  const sfxOn = SFX.enabled !== false;
  const clickVol = SFX.clickVolume ?? 0.45;
  const scratchVol = SFX.scratchVolume ?? 0.16;
  const accent = CAP.accent || DEFAULT_ACCENT;
  const lineStyles = useMemo(() => lineStylesFor(accent), [accent]);
  return (
    <AbsoluteFill>
      {(captionCues || []).map((cue) => {
        const from = Math.round((cue.startMs / 1000) * fps);
        const end = Math.round((cue.endMs / 1000) * fps);
        const dur = Math.max(2, Math.min(end, durationInFrames) - from);
        if (dur <= 0) return null;
        const isSolo = cue.preset === 'SOLO_BIG' || cue.preset === 'SOLO_OUTLINE';
        const isCircled = cue.preset === 'SOLO_OUTLINE';
        return (
          <Sequence key={cue.i} from={from} durationInFrames={dur} layout="none">
            <Cue cue={cue} cueDurationFrames={dur} offsetY={offsetY} fontScale={fontScale} lineStyles={lineStyles} />
            {sfxOn && isSolo ? <Audio src={assetUrl(assetBase, 'sfx/caption-click.mp3')} volume={clickVol} /> : null}
            {sfxOn && isCircled ? (
              <Sequence from={2} layout="none">
                <Audio src={assetUrl(assetBase, 'sfx/caption-scratch.mp3')} volume={scratchVol} />
              </Sequence>
            ) : null}
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};
